import api from './api';

export interface DashboardMetrics {
  // Admin metrics
  total_customers?: number;
  active_customers?: number;
  total_leads?: number;
  pending_leads?: number;
  active_orders?: number;
  monthly_revenue?: number;
  active_sales_officers?: number;
  conversion_rate?: number;
  today_scheduled_calls_count?: number;
  // Manager metrics
  team_customers?: number;
  team_leads?: number;
  team_tenders?: number;
  // Executive metrics
  my_customers?: number;
  my_leads?: number;
  my_tenders?: number;
  // Manager & executive — orders posted by this user (sales_officer_id)
  my_orders_this_month?: number;
  my_orders_total?: number;
}

export interface ScheduledCall {
  id: number;
  entity_type: 'lead' | 'customer' | 'tender';
  entity_id: number;
  entity_name: string;
  contact_no: string;
  scheduled_date: string;
  scheduled_time: string;
  assigned_to_name?: string;
  assigned_to_phone?: string;
  called_by_name?: string;
  called_by_phone?: string;
  status?: string;
  notes?: string;
  call_type: 'scheduled' | 'next_call' | 'lead_next_call';
  is_missed: boolean;
}

export interface UserInteraction {
  id: number;
  name: string;
  calls_completed_today: number;
  scheduled_calls_completed: number;
  scheduled_calls_missed: number;
  scheduled_calls_pending: number;
}

export interface CustomerStatusDistribution {
  status: string;
  count: number;
}

export interface RecentActivity {
  type: string;
  id: number;
  activity_date: string;
  description: string;
  called_by_name?: string | null;
  logged_by_name?: string | null;
  entity_name?: string | null;
  entity_type?: string | null;
  entity_id?: number | null;
  call_status?: string | null;
}

export interface TargetSummaryWeek {
  week: number;
  start_date: string;
  end_date: string;
  weekly_target: number;
  achieved: number;
}

export interface TargetSummary {
  year: number;
  month: number;
  month_target: number;
  month_achieved: number;
  weeks: TargetSummaryWeek[];
}

export interface TargetVsAchievementUser {
  user_id: number;
  name: string;
  target: number;
  achieved: number;
}

export interface TargetVsAchievement {
  year: number;
  month: number;
  users: TargetVsAchievementUser[];
}

export interface TargetVsAchievementBrand {
  brand_id: number;
  name: string;
  target: number;
  achieved: number;
}

export interface TargetVsAchievementByBrand {
  year: number;
  month: number;
  brands: TargetVsAchievementBrand[];
}

export interface DashboardData {
  metrics: DashboardMetrics;
  today_scheduled_calls: ScheduledCall[];
  user_interactions?: UserInteraction[];
  customer_status_distribution?: CustomerStatusDistribution[];
  recent_activities?: RecentActivity[];
  target_summary?: TargetSummary;
  target_vs_achievement?: TargetVsAchievement;
  target_vs_achievement_by_brand?: TargetVsAchievementByBrand;
  target_vs_achievement_by_executive?: TargetVsAchievement;
}

export const dashboardService = {
  getDashboard: async (params?: { year?: number; month?: number }): Promise<DashboardData> => {
    const q = new URLSearchParams();
    if (params?.year != null) q.set('year', String(params.year));
    if (params?.month != null) q.set('month', String(params.month));
    const query = q.toString();
    const response = await api.get('/api/dashboard' + (query ? '?' + query : ''));
    return response.data;
  },

  dismissScheduledCall: async (payload: { entity_type: string; entity_id: number }): Promise<void> => {
    await api.post('/api/dashboard/scheduled-calls/dismiss', payload);
  },
};

